import { css } from '@emotion/react';
import { faPrint } from '@fortawesome/pro-solid-svg-icons';
import {
  type FC, useCallback,
} from 'react';
import { useDispatch } from 'react-redux';
import { AlertComponent } from '~/_shared/baseComponents/alert';
import {
  ButtonComponent, ButtonStyle,
} from '~/_shared/baseComponents/buttons';
import { ModalComponent } from '~/_shared/components/modal/modal.component';
import { OverlayLoaderComponent } from '~/_shared/components/overlay/overlayLoader.component';
import {
  ScrollBarComponent, ScrollbarType,
} from '~/_shared/components/scrollbar/scrollbar.component';
import { useIntercom } from '~/_shared/utils/3rdPartyIntegrations/intercom/intercom.context';
import { useTranslation } from '~/_shared/utils/hooks';
import { ExportReportType } from '~/exportReport/exportReportType';
import { useExportReportData } from '~/exportReport/useExportReportData';
import { useExportReportParams } from '~/exportReport/useExportReportParams';
import { MapExportReportPreviewDriveTimePolygonComponent } from '~/map/page/exportReportPreview/mapExportReportPreviewDriveTimePolygon.component';
import { MapExportReportPreviewLocationComponent } from '~/map/page/exportReportPreview/mapExportReportPreviewLocation.component';
import { MapExportReportPreviewPolygonComponent } from '~/map/page/exportReportPreview/mapExportReportPreviewPolygon.component';
import { MapExportReportPreviewRadiusComponent } from '~/map/page/exportReportPreview/mapExportReportPreviewRadius.component';
import type { ModalProps } from '~/modal/modalType.enum';
import { createReportCreateRequest } from '~/store/frontendState/createReport/createReport.actionCreators';
import { exportReportDisable } from '~/store/mapSettings/toolsState/exportReport/exportReportToolState.actionCreators';
import { useToolsStateExportReportSelector } from '~/store/mapSettings/toolsState/exportReport/exportReportToolState.selectors';
import { useMapIdSelector } from '~/store/selectors/useMapIdSelector';

export type MapExportReportPreviewModalModalProps = ModalProps<{
  onBackToReportsClick?: () => void;
}>;

const previewWrapperStyle = css({
  minWidth: 1000,
});

const alertWrapperStyle = css({
  padding: 20,
});

const modalContentStyle = (isLoading: boolean) => css({
  padding: 0,
  position: 'relative',
  maxHeight: isLoading ? '100%' : undefined,
  overflow: isLoading ? 'hidden' : undefined,
});

export const MapExportReportPreviewModalContainer: FC<MapExportReportPreviewModalModalProps> = ({ onClose, isOpen, onBackToReportsClick }) => {
  const exportReportState = useToolsStateExportReportSelector();
  const mapId = useMapIdSelector();
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const { showNewMessage } = useIntercom();
  const { tableColumns } = useExportReportParams();

  const { isLoading, getDemographicData, getDemographicLabel, isError, isReportEmpty } = useExportReportData({
    tableColumns,
  });

  const handleClose = useCallback(() => {
    dispatch(exportReportDisable());
    onClose();
  }, [dispatch, onClose]);

  const handlePrint = useCallback(() => {
    if (!mapId) {
      return;
    }

    dispatch(createReportCreateRequest(mapId));
    onClose();
  }, [mapId, dispatch, onClose]);

  const handleBackToReportsClick = useCallback(() => {
    onBackToReportsClick?.();
    onClose();
  }, [onBackToReportsClick, onClose]);

  const handlePurchaseDataPackageClick = useCallback(() => {
    // TODO: Add the actual copy.
    showNewMessage('<Insert copy here mybe even einclude the data package name?>');
  }, [showNewMessage]);

  if (!exportReportState.isActive) {
    return null;
  }

  return (
    <ModalComponent
      isOpen={isOpen}
      onClose={handleClose}
      caption={t('Location Summary')}
      leftFooterContent={exportReportState.settings.isSampleReport ? (
        <ButtonComponent
          text={t('exportReport.sampleReport.backToReports')}
          buttonStyle={ButtonStyle.Secondary}
          onClick={handleBackToReportsClick}
        />
      ) : null}
      confirmButton={exportReportState.settings.isSampleReport ? (
        <ButtonComponent
          text={t('exportReport.sampleReport.purchaseDataPackage')}
          buttonStyle={ButtonStyle.Success}
          onClick={handlePurchaseDataPackageClick}
        />
      ) : (
        <ButtonComponent
          isDisabled={isLoading || isError || isReportEmpty}
          prefixIcon={faPrint}
          text={t('Print Report')}
          onClick={handlePrint}
        />
      )}
      contentStyle={modalContentStyle(isLoading)}
      maxWidth={1100}
    >
      <ScrollBarComponent type={ScrollbarType.Horizontal}>
        <div css={previewWrapperStyle}>
          {(isError || isReportEmpty) && (
            <div css={alertWrapperStyle}>
              <AlertComponent type="danger">
                {isError ? t('Error Exporting a Report') : t('No Data Available for This Area')}
              </AlertComponent>
            </div>
          )}

          {!isError && !isReportEmpty && (
            <>
              {exportReportState.params.type === ExportReportType.Location && (
                <MapExportReportPreviewLocationComponent
                  params={exportReportState.params}
                  template={exportReportState.settings.template}
                  tableColumns={tableColumns}
                  getDemographicData={getDemographicData}
                  getDemographicLabel={getDemographicLabel}
                  isLoading={isLoading}
                />
              )}

              {exportReportState.params.type === ExportReportType.Radius && (
                <MapExportReportPreviewRadiusComponent
                  params={exportReportState.params}
                  template={exportReportState.settings.template}
                  tableColumns={tableColumns}
                  getDemographicData={getDemographicData}
                  getDemographicLabel={getDemographicLabel}
                  isLoading={isLoading}
                  isSampleReport={exportReportState.settings.isSampleReport}
                />
              )}

              {exportReportState.params.type === ExportReportType.Polygon && (
                <MapExportReportPreviewPolygonComponent
                  params={exportReportState.params}
                  template={exportReportState.settings.template}
                  getDemographicData={getDemographicData}
                  getDemographicLabel={getDemographicLabel}
                  isLoading={isLoading}
                />
              )}

              {exportReportState.params.type === ExportReportType.DriveTimePolygon && (
                <MapExportReportPreviewDriveTimePolygonComponent
                  params={exportReportState.params}
                  template={exportReportState.settings.template}
                  getDemographicData={getDemographicData}
                  getDemographicLabel={getDemographicLabel}
                  isLoading={isLoading}
                />
              )}
            </>
          )}
        </div>
      </ScrollBarComponent>

      {isLoading && (
        <OverlayLoaderComponent />
      )}
    </ModalComponent>
  );
};
